import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Home, BookOpen, CalendarDays, Trophy, MessageCircle, FolderOpen, LifeBuoy, Headphones, LogOut, ChevronDown, Menu, X } from 'lucide-react';
import { HcLogo } from './HcLogo';
import { useHelpConnect } from './HelpConnectAuth';
import { hcApi } from './api';
import { CertificateCelebrationHost } from './HcCertificateBits';

const LIVE = [
  { to: '/helpconnect', label: 'Dashboard', icon: Home, end: true, id: 'dashboard' },
  { to: '/helpconnect/courses', label: 'My Courses', icon: BookOpen, id: 'courses' },
  { to: '/helpconnect/messages', label: 'Messages', icon: MessageCircle, id: 'messages' },
  { to: '/helpconnect/certificates', label: 'Certificates', icon: Trophy, id: 'certificates' },
  { to: '/helpconnect/calendar', label: 'Learning Calendar', icon: CalendarDays, id: 'calendar' },
  { to: '/helpconnect/resources', label: 'Resources', icon: FolderOpen, id: 'resources' },
  { to: '/helpconnect/support', label: 'Support', icon: LifeBuoy, id: 'support' },
];
const SOON = [];

const SoonBadge = () => (
  <span className="ml-auto whitespace-nowrap text-[9px] uppercase tracking-wide font-bold bg-white/15 text-white/90 rounded-full px-2 py-0.5">Coming soon</span>
);

const useUnreadMessages = () => {
  const [unread, setUnread] = useState(0);
  const loc = useLocation();
  useEffect(() => {
    const load = () => hcApi.get('/messages/unread-count').then(({ data }) => setUnread(data.unread || 0)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [loc.pathname]);
  return unread;
};

const Sidebar = ({ onNavigate }) => {
  const unread = useUnreadMessages();
  const navigate = useNavigate();
  return (
  <nav className="flex flex-col h-full p-4 gap-1.5" data-testid="hc-sidebar">
    {LIVE.map(({ to, label, icon: Icon, end, id }) => (
      <NavLink key={id} to={to} end={end} onClick={onNavigate} data-testid={`hc-nav-${id}`}
        className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl text-[15px] font-medium transition-colors ${isActive ? 'bg-[#92298d] text-white shadow-inner' : 'text-white/85 hover:bg-[#92298d] hover:text-white'}`}>
        <Icon className="w-5 h-5" />{label}
        {id === 'messages' && unread > 0 && (
          <span className="ml-auto min-w-[22px] h-[22px] px-1.5 rounded-full bg-white text-[#4c124a] text-xs font-bold flex items-center justify-center" data-testid="hc-messages-unread-badge">{unread}</span>
        )}
      </NavLink>
    ))}
    {SOON.map(({ label, icon: Icon, id }) => (
      <div key={id} className="flex items-center gap-2.5 px-4 py-3 rounded-xl text-[15px] text-white/55 cursor-not-allowed" aria-disabled="true" data-testid={`hc-nav-${id}-soon`}>
        <Icon className="w-5 h-5 shrink-0" /><span className="whitespace-nowrap text-[14px]">{label}</span><SoonBadge />
      </div>
    ))}
    <div className="mt-auto rounded-2xl bg-white/10 p-5 text-white">
      <div className="font-bold text-lg">Need Help?</div>
      <p className="text-white/80 text-sm mt-1">We're here to support your learning journey.</p>
      <button onClick={() => { onNavigate?.(); navigate('/helpconnect/support#contact'); }} className="mt-4 w-full flex items-center justify-center gap-2 rounded-xl bg-white/15 py-2.5 text-sm font-semibold text-white hover:bg-white/25 transition-colors" data-testid="hc-contact-support-btn">
        <Headphones className="w-4 h-4" /> Contact Support
      </button>
    </div>
  </nav>
  );
};

const UserMenu = () => {
  const { me, signOut } = useHelpConnect();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const initials = `${me?.first_name?.[0] || ''}${me?.last_name?.[0] || ''}`.toUpperCase();
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-3 rounded-xl px-2 py-1.5 hover:bg-gray-50" data-testid="hc-user-menu">
        <div className="w-11 h-11 rounded-full bg-[#922A8C] text-white flex items-center justify-center font-bold">{initials || 'Me'}</div>
        <div className="text-left hidden sm:block">
          <div className="font-semibold text-[#363636] leading-tight" data-testid="hc-user-name">{me?.first_name} {me?.last_name}</div>
          <div className="text-xs text-gray-500">Participant</div>
        </div>
        <ChevronDown className="w-4 h-4 text-gray-500" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-52 rounded-xl bg-white shadow-xl border border-gray-100 p-2 z-50">
          <div className="px-3 py-2 text-xs text-gray-500 truncate">{me?.email}</div>
          <button onClick={() => { signOut(); navigate('/helpconnect/login', { replace: true }); }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[#363636] hover:bg-[#F6EAF5]" data-testid="hc-sign-out">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
};

export const HcLayout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <div className="hc-root min-h-screen bg-[#F7F5F8]">
      <CertificateCelebrationHost />
      <header className="dark-keep h-20 bg-white border-b border-gray-100 flex items-center justify-between px-4 sm:px-8 sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <button className="lg:hidden p-2 rounded-lg hover:bg-gray-100" onClick={() => setMobileOpen(true)} aria-label="Open menu" data-testid="hc-mobile-menu-btn">
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex flex-col">
            <HcLogo />
            <span className="text-[11px] sm:text-xs font-semibold text-[#363636] mt-0.5 whitespace-nowrap" data-testid="hc-portal-tagline">The HELP Center's Participant Learning Portal</span>
          </div>
        </div>
        <UserMenu />
      </header>
      <div className="flex">
        <aside className="hidden lg:block w-72 shrink-0 bg-[#4c124a] min-h-[calc(100vh-5rem)] sticky top-20 self-start">
          <Sidebar />
        </aside>
        {mobileOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
            <aside className="absolute left-0 top-0 bottom-0 w-72 bg-[#4c124a] pt-4">
              <button className="absolute right-3 top-3 text-white p-1" onClick={() => setMobileOpen(false)} aria-label="Close menu"><X className="w-6 h-6" /></button>
              <div className="pt-8 h-full"><Sidebar onNavigate={() => setMobileOpen(false)} /></div>
            </aside>
          </div>
        )}
        <main className="flex-1 min-w-0 overflow-x-hidden p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
};
